import { z } from 'zod';

import { CHAT_HISTORIES_KEY, LANGUAGE_PREFERENCE_KEY } from './config';
import {
  HistoryMessageSchema,
  LocaleCodeSchema,
  type HistoryMessage,
  type LocaleCode,
} from '../src/shared/contracts';

type HistoryStore = Record<LocaleCode, Record<string, HistoryMessage[]>>;

const HistoriesSchema = z
  .object({
    en: z.record(z.string(), z.array(HistoryMessageSchema)).default({}),
    ru: z.record(z.string(), z.array(HistoryMessageSchema)).default({}),
  })
  .catch({ en: {}, ru: {} });

function readJson<T>(key: string, schema: z.ZodTypeAny, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }

    return schema.parse(JSON.parse(raw)) as T;
  } catch {
    return fallback;
  }
}

export class ChatStore {
  getLanguage(): LocaleCode {
    return readJson(LANGUAGE_PREFERENCE_KEY, LocaleCodeSchema, 'en');
  }

  setLanguage(lang: LocaleCode): void {
    localStorage.setItem(LANGUAGE_PREFERENCE_KEY, JSON.stringify(lang));
  }

  getHistory(lang: LocaleCode, cardId: string): HistoryMessage[] {
    const histories = readJson<HistoryStore>(CHAT_HISTORIES_KEY, HistoriesSchema, { en: {}, ru: {} });
    return histories[lang]?.[cardId] ?? [];
  }

  setHistory(lang: LocaleCode, cardId: string, history: HistoryMessage[]): void {
    const histories = readJson<HistoryStore>(CHAT_HISTORIES_KEY, HistoriesSchema, { en: {}, ru: {} });
    histories[lang] ??= {};
    histories[lang][cardId] = history;
    localStorage.setItem(CHAT_HISTORIES_KEY, JSON.stringify(histories));
  }

  clearHistory(lang: LocaleCode, cardId: string): void {
    this.setHistory(lang, cardId, []);
  }
}
